import { useEffect, useState } from "react";
import { api, type User } from "./lib/api";
import { WaitingGame } from "./components/WaitingGame";
import { Auth } from "./pages/Auth";
import { Home } from "./pages/Home";
import { Room } from "./pages/Room";

/**
 * Tiny path router.
 *
 * Three routes do not justify a routing library — especially not one that has
 * shipped a long line of open-redirect and XSS advisories for functionality
 * this app never uses.
 */
function useRoute() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = (to: string) => {
    window.history.pushState({}, "", to);
    setPath(to);
  };

  return { path, go };
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [waited, setWaited] = useState(false);
  const [midGame, setMidGame] = useState(false);
  const { path, go } = useRoute();

  useEffect(() => {
    // This first call doubles as the wake-up: the API sleeps when the site is
    // idle, and starting it here means the cold start runs while the visitor
    // is still looking at the sign-in screen rather than after they act.
    api
      .me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setReady(true));
  }, []);

  // Long enough that an ordinary load never flashes a game on its way past,
  // short enough that a wait worth filling is filled.
  useEffect(() => {
    if (ready) return;
    const timer = setTimeout(() => setWaited(true), 1100);
    return () => clearTimeout(timer);
  }, [ready]);

  // Someone mid-round is not interrupted the moment the app is ready — they
  // are given the way through and left to finish. Nobody who has not started
  // playing is held up for a single frame.
  if (!ready || midGame) {
    return (
      <div className="auth-wrap">
        <div className="waiting">
          {waited ? (
            <>
              <WaitingGame onBusyChange={setMidGame} />
              <div className="waiting-foot">
                {ready ? (
                  <button className="btn primary" onClick={() => setMidGame(false)}>
                    Continue
                  </button>
                ) : (
                  <span className="hint waiting-note">
                    <span className="spinner" />
                    One moment
                  </span>
                )}
              </div>
            </>
          ) : (
            <span className="spinner" />
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Auth onAuthed={setUser} />;
  }

  const roomMatch = path.match(/^\/r\/([0-9a-f-]{36})$/i);
  if (roomMatch) {
    return (
      <Room
        roomId={roomMatch[1]}
        user={user}
        onLeave={() => go("/")}
        key={roomMatch[1]}
      />
    );
  }

  return (
    <Home
      user={user}
      onOpenRoom={(id) => go(`/r/${id}`)}
      onSignOut={async () => {
        await api.logout().catch(() => {});
        setUser(null);
        go("/");
      }}
    />
  );
}
